import type { CommentData } from '@liveblocks/client'
import { useDeleteComment, useEditComment, useSelf, useUser } from '@liveblocks/react'
import type { ComposerSubmitComment } from '@liveblocks/react-ui/primitives'
import { useState } from 'react'

import { type EventProperties, useTrackCallback } from '@/lib/analytics/events'

import { Comment } from '@/components/lemmala-design-system/comment/comment'
import { CommentActionsMenu } from '@/components/lemmala-design-system/comment/comment-actions-menu'
import {
  ConfirmResult,
  useConfirmModal,
} from '@/components/lemmala-design-system/modal/use-confirm-modal'

import { AddReactionButton } from './add-reaction-button'
import { CommentItemContent } from './comment-item-content'
import { commentAnchorId } from './consts'
import { copyCommentLink } from './copy-comment-link'
import { COMMENT_LABELS } from './labels'
import { useCommentReactions } from './use-comment-reactions'

type CommentItemProps = {
  comment: CommentData
  readOnly?: boolean
  highlighted?: boolean
  isReply?: boolean
  hadReplies?: boolean
  trackingProps?: EventProperties
}

export const CommentItem = ({
  comment,
  readOnly,
  highlighted,
  isReply = false,
  hadReplies = false,
  trackingProps,
}: CommentItemProps) => {
  const { user, isLoading: isAuthorLoading } = useUser(comment.userId)
  const self = useSelf()
  const editComment = useEditComment()
  const deleteComment = useDeleteComment()
  const { confirm } = useConfirmModal()
  const {
    frequentEmojis,
    add: addReaction,
    toggle: toggleReaction,
  } = useCommentReactions(comment, isReply, trackingProps)
  const trackEdited = useTrackCallback('assessment.comment.edited')
  const trackDeleted = useTrackCallback('assessment.comment.deleted')
  const trackLinkCopied = useTrackCallback('assessment.comment.link.copied')

  const [isEditing, setIsEditing] = useState(false)

  const { body } = comment
  if (!body) return null

  const canModify = !readOnly && !!self && self.id === comment.userId
  // Nothing renders while the directory lookup is in flight — initials would flash and then be
  // replaced. The slot keeps its footprint so the name doesn't shift when the avatar arrives.
  const authorName = user?.name ?? (isAuthorLoading ? '' : COMMENT_LABELS.unknownAuthor)
  const authorAvatarSlot = isAuthorLoading ? <div className='size-6 shrink-0' /> : undefined

  const handleSave = ({ body: nextBody }: ComposerSubmitComment) => {
    if (JSON.stringify(nextBody) !== JSON.stringify(body)) {
      editComment({ threadId: comment.threadId, commentId: comment.id, body: nextBody })
      trackEdited({ ...trackingProps, comment_id: comment.id, is_reply: isReply })
    }
    setIsEditing(false)
  }

  const handleCopyLink = async () => {
    await copyCommentLink(comment.id)
    trackLinkCopied({ ...trackingProps, comment_id: comment.id, is_reply: isReply })
  }

  const handleDelete = async () => {
    const result = await confirm({
      variant: 'destructive',
      title: COMMENT_LABELS.deleteConfirm,
      subtitle: COMMENT_LABELS.deleteIrreversible,
    })
    if (result !== ConfirmResult.CONFIRMED) return

    deleteComment({ threadId: comment.threadId, commentId: comment.id })
    trackDeleted({
      ...trackingProps,
      comment_id: comment.id,
      is_reply: isReply,
      had_replies: hadReplies,
    })
  }

  return (
    <Comment
      authorName={authorName}
      authorAvatar={user?.avatar}
      avatar={authorAvatarSlot}
      createdAt={comment.createdAt}
      anchorId={commentAnchorId(comment.id)}
      highlighted={highlighted}
      editedLabel={comment.editedAt ? COMMENT_LABELS.edited : undefined}
      actions={
        isEditing ? undefined : (
          <div className='flex items-center gap-0.5'>
            {!readOnly && (
              <AddReactionButton onEmojiSelect={addReaction} frequentEmojis={frequentEmojis} />
            )}
            <CommentActionsMenu
              canModify={canModify}
              onEdit={() => setIsEditing(true)}
              onDelete={handleDelete}
              onCopyLink={handleCopyLink}
            />
          </div>
        )
      }
    >
      <CommentItemContent
        isEditing={isEditing}
        body={body}
        reactions={comment.reactions}
        currentUserId={self?.id}
        readOnly={readOnly}
        onSave={handleSave}
        onToggleReaction={toggleReaction}
        onCancelEdit={() => setIsEditing(false)}
      />
    </Comment>
  )
}
